import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

interface UseTypewriterOptions {
  isGenerating?: boolean;
  maxCatchUpMultiplier?: number;
  speed?: number;
}

export const useTypewriter = (targetContent: string, options: UseTypewriterOptions = {}) => {
  const { isGenerating = false, maxCatchUpMultiplier = 10, speed = 1.5 } = options;

  const displayedLengthRef = useRef(0);
  const listenerRef = useRef<(() => void) | null>(null);
  const snapshotRef = useRef('');

  const updateSnapshot = useCallback(() => {
    snapshotRef.current = targetContent.slice(0, displayedLengthRef.current);
    listenerRef.current?.();
  }, [targetContent]);

  const displayedContent = useSyncExternalStore(
    (onStoreChange) => {
      listenerRef.current = onStoreChange;
      return () => {
        listenerRef.current = null;
      };
    },
    () => snapshotRef.current,
  );

  useEffect(() => {
    // If not generating, or if target is actually shorter (unexpected jump),
    // sync immediately and stop animation.
    if (!isGenerating || targetContent.length < displayedLengthRef.current) {
      displayedLengthRef.current = targetContent.length;
      updateSnapshot();
      return;
    }

    let requestId: number | undefined;

    const animate = () => {
      const currentLength = displayedLengthRef.current;
      if (currentLength < targetContent.length) {
        const delta = targetContent.length - currentLength;

        // Speed up if we are lagging behind a large chunk
        const catchUpFactor = Math.min(maxCatchUpMultiplier, Math.max(1, delta / 20));

        const charsToAdd = Math.ceil(speed * catchUpFactor);
        displayedLengthRef.current = Math.min(currentLength + charsToAdd, targetContent.length);
        updateSnapshot();
      }
      requestId = requestAnimationFrame(animate);
    };

    requestId = requestAnimationFrame(animate);

    return () => {
      if (requestId) {
        cancelAnimationFrame(requestId);
      }
    };
  }, [targetContent, isGenerating, speed, maxCatchUpMultiplier, updateSnapshot]);

  return displayedContent;
};
